/**
 * catalog-pick — 카탈로그 탭 → 등록 폼으로 행 하나를 건네는 길(VOCABULARY "제품 등록 — 한 폼", 2026-10-22).
 * `BroadcastChannel('catalog-pick')` 하나. 폼이 `/catalog?pick=1` 을 새 탭으로 열고 듣는다 · 카탈로그 탭은 [확인] 에 보내고 제 탭을 닫는다.
 */
import { CatalogPickNoneSchema, CatalogPickSchema, CatalogPickSearchSchema } from '../api/client';
import type { CatalogPick, CatalogPickNone, CatalogPickSearch, ECatalogTable } from '../api/client';
import { assert } from './assert';

/** 채널 이름 — 보내는 쪽 · 듣는 쪽이 같은 글을 본다 */
export const CATALOG_PICK_CHANNEL = 'catalog-pick';
/** 다시 찾기 채널 — 행 담기와 가른다(폼은 catalog-pick 에 CatalogPick 만 온다고 assert 한다) */
export const CATALOG_PICK_SEARCH_CHANNEL = 'catalog-pick-search';
/** 원하는 결과 없음 채널 — 행 담기와 가른다(같은 까닭) */
export const CATALOG_PICK_NONE_CHANNEL = 'catalog-pick-none';
/** 폼이 새 탭으로 여는 카탈로그 주소 — `pick=1` 이 pick 모드 */
export const CATALOG_PICK_URL = '/catalog?pick=1';
/** 카탈로그 페이지의 경로 — 이미 열린 탭이 거기 있으면 다시 찾기로(주소를 안 바꾼다) */
const CATALOG_PATH = '/catalog';
/** 그 탭의 이름 — [찾기] 를 또 누르면 새 탭이 아니라 이 이름의 탭을 다시 쓴다(현민 2026-10-22 — 왔다 갔다 할 때 탭이 쌓이지 않게) */
export const CATALOG_PICK_WINDOW_NAME = 'catalog-pick';
/** 제조사 열 — 부품 표는 vendor, cpu · gpu · hba · nic 는 oem_vendor(parts V장 `VendorColumnOf`). 앞에 있는 것을 쓴다 */
const VENDOR_COLUMNS: readonly string[] = ['vendor', 'oem_vendor'];
/** 설명 열 — 약식이 비면 제조사 원문 */
const DESCRIPTION_COLUMNS: readonly string[] = ['description', 'description_raw'];

/**
 * 등록 폼이 카탈로그 탭을 연다 — `window.open(CATALOG_PICK_URL + &q=<글>, CATALOG_PICK_WINDOW_NAME)` 뒤 `focus()`.
 * 약속: 폼 밖의 새 탭(같은 탭 이동 아님 — 현민 "새 탭으로 띄워져서"). 그 이름의 탭이 이미 카탈로그에 있으면 주소를 안 바꾸고 **다시 찾기를 방송해 그 자리에서 검색하고 앞으로 온다**(2026-10-26 — 주소를 바꾸면 통째로 다시 읽혀 앞 결과가 보였다)
 * — [선택] 으로 닫힌 뒤엔 다시 새 탭. 스크립트로 연 탭이라 카탈로그 쪽 `window.close()` 가 통한다. 찾기 글이 비면 `q` 없이 — 카탈로그 탭에서 친다.
 * 팝업이 막히면(null) false — 부르는 쪽이 사람에게 알린다(조회를 기다린 뒤 열면 막힐 수 있다 · 2026-10-26). 앞으로 오는 것은 브라우저 몫 — 안 오면 뒤에 남을 뿐 동작은 같다.
 */
export function openCatalogPickTab(query: string): boolean {
  const trimmed = query.trim();
  const tab = window.open('', CATALOG_PICK_WINDOW_NAME);   // 그 이름의 탭이 있으면 그 탭, 없으면 빈 새 탭
  if (tab === null) return false;
  if (isCatalogTab(tab)) sendCatalogPickSearch(trimmed);   // 이미 카탈로그 — 다시 읽지 않고 그 자리에서
  else tab.location.href = trimmed === '' ? CATALOG_PICK_URL : `${CATALOG_PICK_URL}&q=${encodeURIComponent(trimmed)}`;
  tab.focus();
  return true;
}

/** 다시 찾기를 방송한다 — 채널을 열어 보내고 바로 닫는다(sendCatalogPick 꼴) */
export function sendCatalogPickSearch(query: string): void {
  const channel = new BroadcastChannel(CATALOG_PICK_SEARCH_CHANNEL);
  const search: CatalogPickSearch = { q: query };
  channel.postMessage(search);
  channel.close();
}

/** 카탈로그 탭(pick 모드)이 다시 찾기를 듣는다 — Zod 로 거르고 꼴이 아니면 assert. 돌려주는 함수가 채널을 닫는다 */
export function listenCatalogPickSearch(onSearch: (search: CatalogPickSearch) => void): () => void {
  const channel = new BroadcastChannel(CATALOG_PICK_SEARCH_CHANNEL);
  channel.onmessage = (event: MessageEvent<unknown>): void => {
    const parsed = CatalogPickSearchSchema.safeParse(event.data);
    assert(parsed.success, `catalog-pick-search 채널에는 CatalogPickSearch 만 온다 — 보내는 쪽은 sendCatalogPickSearch 하나뿐: ${JSON.stringify(event.data)}`);
    onSearch(parsed.data);
  };
  return (): void => channel.close();
}

/** 원하는 결과 없음을 방송한다 — 채널을 열어 보내고 바로 닫는다(sendCatalogPick 꼴). 제 탭을 닫는 건 부르는 쪽 몫 */
export function sendCatalogPickNone(query: string): void {
  const channel = new BroadcastChannel(CATALOG_PICK_NONE_CHANNEL);
  const none: CatalogPickNone = { q: query };
  channel.postMessage(none);
  channel.close();
}

/** 등록 폼이 원하는 결과 없음을 듣는다 — Zod 로 거르고 꼴이 아니면 assert. 돌려주는 함수가 채널을 닫는다 */
export function listenCatalogPickNone(onNone: (none: CatalogPickNone) => void): () => void {
  const channel = new BroadcastChannel(CATALOG_PICK_NONE_CHANNEL);
  channel.onmessage = (event: MessageEvent<unknown>): void => {
    const parsed = CatalogPickNoneSchema.safeParse(event.data);
    assert(parsed.success, `catalog-pick-none 채널에는 CatalogPickNone 만 온다 — 보내는 쪽은 sendCatalogPickNone 하나뿐: ${JSON.stringify(event.data)}`);
    onNone(parsed.data);
  };
  return (): void => channel.close();
}

/** 폼 탭으로 돌아가며 이 탭을 닫는다 — 연 탭(opener)에 포커스를 준 뒤 close(2026-10-26 — close 만 하면 브라우저가 옆 탭으로 갔다). 주소로 직접 연 탭은 opener 가 없어 닫기만(안 닫힐 수도) */
export function closeCatalogPickTab(): void {
  const opener: Window | null = window.opener as Window | null;
  if (opener !== null) opener.focus();
  window.close();
}

/** 그 탭이 이미 카탈로그 페이지인가 — 새 빈 탭은 about:blank. 다른 출처로 떠났으면 주소를 못 읽어(브라우저가 throw) 아님으로 */
function isCatalogTab(tab: Window): boolean {
  try {
    return tab.location.pathname === CATALOG_PATH;
  } catch {
    return false;
  }
}

/**
 * 카탈로그 탭(pick 모드)이 고른 행을 방송한다.
 * 약속: 채널을 열어 `pick` 을 postMessage 하고 바로 닫는다. 듣는 탭이 없어도 조용하다(방송). 제 탭을 닫는 건 부르는 쪽(CatalogSearchView) 몫.
 */
export function sendCatalogPick(pick: CatalogPick): void {
  const channel = new BroadcastChannel(CATALOG_PICK_CHANNEL);
  channel.postMessage(pick);
  channel.close();
}

/**
 * 등록 폼이 방송을 듣는다.
 * 약속: 채널을 열어 message 마다 `CatalogPickSchema` 로 거른 뒤 `onPick` — 꼴이 아니면 assert(우리 코드끼리라 있을 수 없는 상태).
 * 돌려주는 함수가 채널을 닫는다 — 폼의 onUnmount 가 부른다(안 부르면 탭을 옮겨도 듣는다).
 */
export function listenCatalogPick(onPick: (pick: CatalogPick) => void): () => void {
  const channel = new BroadcastChannel(CATALOG_PICK_CHANNEL);
  channel.onmessage = (event: MessageEvent<unknown>): void => {
    const parsed = CatalogPickSchema.safeParse(event.data);
    assert(parsed.success, `catalog-pick 채널에는 CatalogPick 만 온다 — 보내는 쪽은 sendCatalogPick 하나뿐인데 다른 꼴이 왔다: ${JSON.stringify(event.data)}`);
    onPick(parsed.data);
  };
  return (): void => channel.close();
}

/**
 * 카탈로그 행(`fetchCatalogRow` 가 준 열 → 값) → 건넬 행 하나.
 * 약속: `id` 정수 · `option_part` 글이어야 한다(아니면 assert — 표 17 은 전부 NOT NULL). `vendor`(cpu · gpu · hba · nic 는 `oem_vendor`) ·
 * `description`(약식, 비면 `description_raw`) · `chassis_model_id`(chassis 표만) 는 값이 없으면 null.
 */
export function catalogPickOf(table: ECatalogTable, row: Record<string, unknown>): CatalogPick {
  const id = integerOrNull(row, 'id');
  const optionPart = firstTextOrNull(row, ['option_part']);
  assert(id !== null && optionPart !== null, `카탈로그 표 17 의 id · option_part 는 NOT NULL — ${table} 행에 빈 값이 왔다: ${JSON.stringify({ id: row['id'], option_part: row['option_part'] })}`);
  return {
    table,
    id,
    option_part: optionPart,
    vendor: firstTextOrNull(row, VENDOR_COLUMNS),
    description: firstTextOrNull(row, DESCRIPTION_COLUMNS),
    chassis_model_id: integerOrNull(row, 'chassis_model_id'),
  };
}

/** 이름 순서대로 첫 번째 빈 글 아닌 값. 없으면 null */
function firstTextOrNull(row: Record<string, unknown>, names: readonly string[]): string | null {
  for (const name of names) {
    const value = row[name];
    if (typeof value === 'string' && value.trim() !== '') return value;
  }
  return null;
}

/** 정수 값 — 없거나 정수가 아니면 null. 옛 표 API(`fetchCatalogRow`)는 id 를 글("913")로 주므로 숫자 글도 받는다(`openHit` 의 `Number()` 와 같은 읽기) */
function integerOrNull(row: Record<string, unknown>, name: string): number | null {
  const value = row[name];
  const number = typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value) : NaN;
  return Number.isInteger(number) ? number : null;
}
